import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button } from '@kontentino/ui';
import Modal from 'components/shared/Modal';
import { useTranslation } from 'react-i18next';
import Posts from 'components/workflowOptions/postsSelection/Posts';
import { ListPost, PostDetail } from 'types/PostDetail';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import PostApi from 'api/post';
import { useUser } from 'modules/user/userSelector';
import { PostApprovalWorkflowPermissions } from 'utils/post/permissions';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import Skeleton from 'react-loading-skeleton';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (posts: ListPost[]) => void;
  isScheduleLoading?: boolean;
  localPosts: PostDetail['localPosts'];
};

const PostOverviewLocalPostsSchedulePosts: FC<Props> = ({
  isOpen,
  onClose,
  onSchedule,
  isScheduleLoading,
  localPosts = [],
}) => {
  const { availableStatuses } = useUser();

  const [selectedPosts, setSelectedPosts] = useState<ListPost[]>([]);
  const { t } = useTranslation();
  const localPostsData = Array.isArray(localPosts) ? [] : localPosts.data;

  const postIds = localPostsData.map((selectedPost) => selectedPost.id);

  const {
    data: validationData,
    isLoading: isValidationLoading,
    isFetching,
  } = useQuery(
    [queryKey.multipleScheduleValidations, postIds],
    () => PostApi.validate(postIds),
    {
      enabled: isOpen,
    },
  );

  function getAllowedStatuses() {
    return new Set(
      availableStatuses(localPostsData.map((post) => post.page.type)).filter(
        (status) => {
          return PostApprovalWorkflowPermissions(status as PostStatus)
            .canSchedule;
        },
      ),
    );
  }

  useEffect(() => {
    if (isOpen) {
      setSelectedPosts([]);
    }
  }, [isOpen]);

  const allowedStatuses = getAllowedStatuses();
  const filteredLocalPostsData = localPostsData.filter((post) =>
    allowedStatuses.has(post.status),
  );

  const warningMessages = useMemo(() => {
    const messages: Record<number, string> = {};

    if (validationData) {
      validationData.forEach((value) => {
        if (!!value.messages.errors.length) {
          messages[value.postId] = value.messages.errors[0].message;
        }
      });
    }

    return messages;
  }, [validationData]);

  const disabledPosts = useMemo(() => {
    return (validationData ?? [])
      .filter((value) => value.messages.errors.length)
      .map((value) => value.postId);
  }, [validationData]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      classNames={{
        modal: '!tw-max-w-[480px]',
      }}
    >
      <Modal.Header title={t('schedulePosts')} />
      <Modal.Content className="tw-flex tw-flex-col tw-gap-4 tw-px-1 tw-pb-4">
        {(isValidationLoading || isFetching) && (
          <div className="tw-h-full tw-px-6">
            <Skeleton height={100} width="100%" count={4} />
          </div>
        )}

        {!isValidationLoading && filteredLocalPostsData.length === 0 && (
          <div className="tw-text-gray-500 tw-py-4 tw-text-center tw-text-sm">
            <p>{t('noPostsToSchedule')}</p>
          </div>
        )}

        {!isFetching &&
          !isValidationLoading &&
          filteredLocalPostsData.length > 0 && (
            <Posts
              posts={filteredLocalPostsData}
              selectedPosts={selectedPosts}
              warningMessages={warningMessages}
              onSelectPost={(post) =>
                setSelectedPosts((prevState) =>
                  prevState.find(({ id }) => id === post.id)
                    ? prevState.filter(({ id }) => id !== post.id)
                    : [...prevState, post],
                )
              }
              onSelectAllPosts={() =>
                setSelectedPosts(
                  filteredLocalPostsData.filter(
                    (post) => !disabledPosts.includes(post.id),
                  ),
                )
              }
              onUnselectAllPosts={() => setSelectedPosts([])}
              disabledPosts={disabledPosts}
            />
          )}
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={() => onSchedule(selectedPosts)}
          disabled={
            isValidationLoading ||
            selectedPosts.length === 0 ||
            filteredLocalPostsData.length === 0
          }
          isLoading={isScheduleLoading}
        >
          {t('schedule')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
export default PostOverviewLocalPostsSchedulePosts;
